import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import ReciboVentaDocumento from './ReciboVentaDocumento';

const BASE_NOTAS = '/api/facturacion/notas-venta';
const BASE_FACTURAS = '/api/facturacion/facturas';
const CONSUMIDOR_FINAL = '9999999999999';

const dinero = (n) => `$${(Math.round((Number(n) || 0) * 100) / 100).toFixed(2)}`;
const nuevoItem = (iva = 15) => ({ descripcion: '', cantidad: 1, precio: '', descuento: 0, iva });

const FORMAS_PAGO = {
    '01': 'Sin utilización del sistema financiero (Efectivo)',
    '16': 'Tarjeta de Débito',
    '19': 'Tarjeta de Crédito',
    '20': 'Otros con utilización del sistema financiero (Transferencia / Depósito)',
    '17': 'Dinero Electrónico',
};

const DATOS_SUCURSAL = {
    sur: {
        nombre: 'Sucursal Sur',
        direccion: 'AV. VEINTIMILLA Y CAMILO PONCE',
        telefono: '0993387008'
    },
    norte: {
        nombre: 'Sucursal Norte (Matriz)',
        direccion: 'Entre Arellano y Ayacucho',
        telefono: '0998940200'
    }
};

export const getDatosSucursal = (sucursalStr) => {
    const s = (sucursalStr || '').toLowerCase();
    if (s.includes('nor') || s.includes('matriz')) return DATOS_SUCURSAL.norte;
    return DATOS_SUCURSAL.sur;
};

const normalizarSucursal = (s) => {
    const low = (s || '').toLowerCase();
    if (low.includes('nor') || low.includes('matriz')) return 'Tulcán Norte';
    if (low.includes('sur')) return 'Tulcán Sur';
    return s || '';
};

/**
 * Ventana para emitir o ver una Nota de Venta interna (sin envío al SRI).
 * Diseñada con la misma estructura, estética y comportamiento que ModalFactura.
 */
const ModalNotaVenta = ({
    paciente = null,
    notaExistente = null,
    sucursalUsuario = '',
    items: itemsIniciales = null,
    origen = 'manual',
    origenId = '',
    onEmitida,
    onClose
}) => {
    const [sucursal, setSucursal] = useState(() => {
        if (notaExistente?.sucursal) return normalizarSucursal(notaExistente.sucursal);
        if (sucursalUsuario) return normalizarSucursal(sucursalUsuario);
        return 'Tulcán Sur';
    });
    const [consumidorFinal, setConsumidorFinal] = useState(
        notaExistente ? (notaExistente.cliente_identificacion === CONSUMIDOR_FINAL) : !paciente
    );
    const [cliente, setCliente] = useState({
        identificacion: notaExistente?.cliente_identificacion || paciente?.cedula || (consumidorFinal ? CONSUMIDOR_FINAL : ''),
        nombre: notaExistente?.cliente_nombre || (paciente ? `${paciente.first_name || ''} ${paciente.last_name || ''}`.trim().toUpperCase() : (consumidorFinal ? 'CONSUMIDOR FINAL' : '')),
        direccion: notaExistente?.cliente_direccion || paciente?.address || '',
        email: notaExistente?.cliente_email || paciente?.email || '',
        telefono: notaExistente?.cliente_telefono || paciente?.phone || '',
    });
    const [pacienteId, setPacienteId] = useState(notaExistente?.patient || paciente?.id || null);
    const [busqueda, setBusqueda] = useState('');
    const [resultados, setResultados] = useState([]);
    const [items, setItems] = useState(
        notaExistente?.items?.length ? notaExistente.items : (itemsIniciales?.length ? itemsIniciales : [nuevoItem(15)])
    );
    const [formaPago, setFormaPago] = useState(notaExistente?.forma_pago || '01');
    const [observaciones, setObservaciones] = useState(notaExistente?.observaciones || '');
    const [errores, setErrores] = useState({});
    const [mensaje, setMensaje] = useState(null);
    const [emitiendo, setEmitiendo] = useState(false);
    const [notaEmitida, setNotaEmitida] = useState(notaExistente || null);
    const [autocompletado, setAutocompletado] = useState(false);

    const [busquedaCat, setBusquedaCat] = useState('');
    const [catalogo, setCatalogo] = useState([]);
    const ticketRef = useRef(null);

    // Formato de comprobante a mostrar: 'recibo' (Talonario oficial) o 'ticket' (POS térmico)
    const [formatoVista, setFormatoVista] = useState('recibo');

    // Abono y saldo para el recibo óptico
    const [abono, setAbono] = useState(notaExistente?.abono !== undefined ? Number(notaExistente.abono) : null);
    const [mostrarDetalleOptico, setMostrarDetalleOptico] = useState(false);
    const [datosRecibo, setDatosRecibo] = useState(notaExistente?.recibo_datos || {
        armazon: '',
        esfera_od: '', cilindro_od: '', eje_od: '', adicion_od: '', dp_od: '',
        esfera_oi: '', cilindro_oi: '', eje_oi: '', adicion_oi: '', dp_oi: '',
        tipo_lente: [],
        materiales: [],
        caracteristicas: [],
        observaciones: '',
    });

    // Al reabrir una nota se muestra su recibo tal como está guardado, con las correcciones que tenga.
    useEffect(() => {
        if (!notaExistente?.id) return;
        api.get(`${BASE_NOTAS}/${notaExistente.id}/recibo/`)
            .then(({ data }) => setNotaEmitida(prev => ({
                ...prev,
                recibo_numero: data.recibo.numero,
                recibo_datos: data.recibo,
                abono: data.recibo.abono,
                saldo: data.recibo.saldo,
            })))
            .catch(() => {}); // Notas anteriores al talonario: no tienen recibo.
    }, [notaExistente]);

    // Obtener la sucursal del usuario emisor si no vino por prop
    useEffect(() => {
        if (!notaExistente && !sucursalUsuario) {
            api.get(`${BASE_NOTAS}/opciones/`)
                .then(r => {
                    if (r.data?.sucursal) {
                        setSucursal(normalizarSucursal(r.data.sucursal));
                    }
                })
                .catch(() => {});
        }
    }, [notaExistente, sucursalUsuario]);

    useEffect(() => {
        if (sucursalUsuario && !notaExistente) {
            setSucursal(normalizarSucursal(sucursalUsuario));
        }
    }, [sucursalUsuario, notaExistente]);

    // Función para autocompletar tratamientos del paciente
    const cargarTratamientos = async (pid) => {
        if (!pid) return;
        try {
            const { data } = await api.get(`${BASE_FACTURAS}/tratamientos-paciente/`, { params: { patient_id: pid } });
            if (data?.items?.length > 0) {
                setItems(data.items);
                setAutocompletado(true);
            }
        } catch (e) {
            console.warn('Error cargando tratamientos del paciente:', e);
        }
    };

    // Si viene un paciente precargado, autocompletar tratamientos
    useEffect(() => {
        if (paciente?.id && (!itemsIniciales || itemsIniciales.length === 0) && !notaExistente) {
            cargarTratamientos(paciente.id);
        }
    }, [paciente]); // eslint-disable-line react-hooks/exhaustive-deps

    // Buscar paciente para precargar cliente
    useEffect(() => {
        if (busqueda.trim().length < 2) { setResultados([]); return undefined; }
        const t = setTimeout(() => {
            api.get(`/api/patients/?search=${encodeURIComponent(busqueda.trim())}`)
                .then(r => setResultados((r.data.results || r.data || []).slice(0, 6)))
                .catch(() => setResultados([]));
        }, 300);
        return () => clearTimeout(t);
    }, [busqueda]);

    // Catálogo de servicios y productos
    useEffect(() => {
        const t = setTimeout(() => {
            api.get(`${BASE_FACTURAS}/catalogo/`, { params: { q: busquedaCat.trim() } })
                .then(r => setCatalogo(r.data || []))
                .catch(() => setCatalogo([]));
        }, 250);
        return () => clearTimeout(t);
    }, [busquedaCat]);

    const agregarDelCatalogo = (c) => {
        const linea = {
            codigo: c.codigo,
            descripcion: c.descripcion,
            cantidad: 1,
            precio: Number(c.precio) || '',
            descuento: 0,
            iva: c.iva !== undefined ? c.iva : 15,
            ...(c.producto_id ? { producto_id: c.producto_id, stock: Number(c.stock) } : {})
        };
        setItems(prev => (prev.length === 1 && !prev[0].descripcion && !prev[0].precio ? [linea] : [...prev, linea]));
        setBusquedaCat('');
        setErrores(prev => ({ ...prev, items: undefined }));
    };

    const elegirPaciente = (p) => {
        setPacienteId(p.id);
        setConsumidorFinal(false);
        setCliente({
            identificacion: p.cedula || '',
            nombre: `${p.first_name || ''} ${p.last_name || ''}`.trim().toUpperCase(),
            direccion: p.address || '',
            email: p.email || '',
            telefono: p.phone || ''
        });
        setBusqueda('');
        setResultados([]);
        cargarTratamientos(p.id);

        // Cargar refracción y datos ópticos para el recibo
        api.get('/api/facturacion/recibos/datos-paciente/', { params: { patient_id: p.id } })
            .then(r => {
                if (r.data) {
                    setDatosRecibo(prev => ({
                        ...prev,
                        ...r.data,
                    }));
                }
            })
            .catch(() => {});
    };

    const cambiarCliente = (campo, valor) => {
        setCliente(prev => ({ ...prev, [campo]: valor }));
        setErrores(prev => ({ ...prev, [campo]: undefined }));
    };

    const cambiarItem = (i, campo, valor) => {
        setItems(prev => prev.map((it, k) => (k === i ? { ...it, [campo]: valor } : it)));
        setErrores(prev => ({ ...prev, [`items.${i + 1}`]: undefined, items: undefined }));
    };

    // Cálculos
    const lineas = items.map(it => {
        const cant = Number(it.cantidad) || 0;
        const prec = Number(it.precio) || 0;
        const desc = Number(it.descuento) || 0;
        const base = Math.max(0, (cant * prec) - desc);
        const ivaVal = Math.round(base * (Number(it.iva) || 0) * 100 / 100) / 100;
        return { base, ivaVal, totalLinea: base + ivaVal };
    });

    const subtotal = lineas.reduce((a, l) => a + l.base, 0);
    const ivaTotal = lineas.reduce((a, l) => a + l.ivaVal, 0);
    const total = subtotal + ivaTotal;

    const emitir = async () => {
        setEmitiendo(true);
        setErrores({});
        setMensaje(null);

        const itemsValidos = items.filter(it => (it.descripcion || '').trim().length > 0);
        if (itemsValidos.length === 0) {
            setErrores({ items: 'Agregue al menos un producto o servicio.' });
            setEmitiendo(false);
            return;
        }

        const abonoVal = abono !== null ? Number(abono) : total;
        const saldoVal = Math.max(0, total - abonoVal);

        try {
            const { data } = await api.post(`${BASE_NOTAS}/emitir/`, {
                patient_id: pacienteId,
                origen,
                origen_id: origenId,
                sucursal,
                forma_pago: formaPago,
                observaciones,
                abono: abonoVal,
                saldo: saldoVal,
                recibo: {
                    ...datosRecibo,
                    total: total,
                    abono: abonoVal,
                    saldo: saldoVal,
                    cliente_nombre: consumidorFinal ? 'CONSUMIDOR FINAL' : cliente.nombre,
                    cliente_telefono: cliente.telefono,
                    cliente_identificacion: cliente.identificacion,
                    recibi_de: consumidorFinal ? 'CONSUMIDOR FINAL' : cliente.nombre,
                },
                cliente: consumidorFinal
                    ? { tipo: '07', identificacion: CONSUMIDOR_FINAL, nombre: 'CONSUMIDOR FINAL' }
                    : { ...cliente, tipo: '07' },
                items: itemsValidos
            });

            setNotaEmitida(data.nota);
            if (data.recibo) {
                setNotaEmitida(prev => ({ ...prev, recibo_numero: data.recibo.numero, recibo_datos: data.recibo }));
            }
            setMensaje({ ok: true, texto: data.aviso_recibo ? `${data.detail} ${data.aviso_recibo}` : data.detail });
            if (onEmitida) onEmitida(data.nota);
        } catch (e) {
            const d = e.response?.data || {};
            setErrores(d.campos || {});
            setMensaje({ ok: false, texto: d.detail || 'No se pudo emitir la nota de venta.' });
        }
        setEmitiendo(false);
    };

    const imprimirTicket = () => {
        window.print();
    };

    const err = (campo) => errores[campo] && <div className="mf-err">* {errores[campo]}</div>;
    const infoSucursal = getDatosSucursal(notaEmitida?.sucursal || sucursal);

    return (
        <div className="mf-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <style>{`
                .mf-overlay { position: fixed; inset: 0; background: rgba(17,24,39,0.7); z-index: 10040; display: flex; align-items: center; justify-content: center; padding: 16px; }
                .mf-modal { background: #fff; border-radius: 16px; width: 1000px; max-width: 100%; max-height: 94vh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px rgba(0,0,0,0.3); }
                .mf-head { background: #111827; color: #fff; padding: 16px 22px; display: flex; align-items: center; justify-content: space-between; }
                .mf-body { padding: 18px 22px; overflow-y: auto; }
                .mf-sec { font-size: 0.75rem; font-weight: 800; letter-spacing: 0.06em; color: #6b7280; text-transform: uppercase; margin: 4px 0 10px; }
                .mf-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px 14px; }
                .mf-f label { display: block; font-size: 0.78rem; font-weight: 700; color: #374151; margin-bottom: 3px; }
                .mf-f input, .mf-f select, .mf-t input, .mf-t select { width: 100%; padding: 8px 10px; border: 1.5px solid #d1d5db; border-radius: 8px; font-size: 0.9rem; box-sizing: border-box; background: #fff; }
                .mf-t select { padding: 8px 6px 8px 8px; min-width: 82px; cursor: pointer; }
                .mf-t input[type="number"] { padding: 8px 6px; }
                .mf-f input.bad, .mf-t input.bad { border-color: #dc2626; background: #fef2f2; }
                .mf-err { color: #dc2626; font-size: 0.78rem; font-weight: 600; margin-top: 2px; }
                .mf-t { width: 100%; border-collapse: collapse; font-size: 0.88rem; min-width: 680px; }
                .mf-t th { text-align: left; font-size: 0.72rem; color: #6b7280; font-weight: 700; padding: 6px 4px; border-bottom: 1px solid #e5e7eb; }
                .mf-t td { padding: 5px 4px; vertical-align: top; }
                .mf-tot { display: flex; justify-content: flex-end; margin-top: 10px; }
                .mf-tot table td { padding: 3px 10px; font-size: 0.92rem; }
                .mf-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px; border-radius: 8px; font-weight: 700; font-size: 0.9rem; cursor: pointer; border: 1.5px solid #065f46; background: #065f46; color: #fff; }
                .mf-btn.sec { background: #fff; color: #111827; border-color: #d1d5db; }
                .mf-btn:disabled { opacity: 0.6; cursor: default; }
                .mf-busca { position: relative; }
                .mf-res { position: absolute; left: 0; right: 0; top: 100%; background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 10px 20px rgba(0,0,0,0.12); z-index: 20; }
                .mf-res div { padding: 8px 10px; cursor: pointer; font-size: 0.88rem; }
                .mf-res div:hover { background: #f3f4f6; }
                .mf-aviso { padding: 10px 12px; border-radius: 8px; font-size: 0.88rem; font-weight: 600; margin-bottom: 12px; }
                @media (max-width: 760px) { .mf-grid { grid-template-columns: 1fr; } }

                /* Vista de impresión para recibo térmico */
                @media print {
                    body * { visibility: hidden; }
                    .nv-print-area, .nv-print-area * { visibility: visible; }
                    .nv-print-area { position: fixed; left: 0; top: 0; width: 100%; margin: 0; padding: 20px; background: #fff; color: #000; }
                    .nv-no-print { display: none !important; }
                }
            `}</style>

            <div className="mf-modal">
                <div className="mf-head nv-no-print">
                    <div style={{ fontWeight: 800, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <i className="bi bi-file-earmark-text"></i>
                        <span>{notaExistente ? `Nota de Venta ${notaExistente.numero}` : 'Nota de Venta (Interna)'}</span>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1.6rem', cursor: 'pointer', lineHeight: 1 }}>&times;</button>
                </div>

                <div className="mf-body">
                    {/* SI YA ESTÁ EMITIDA / MODO VISTA O IMPRESIÓN */}
                    {notaEmitida ? (
                        <div>
                            <div className="nv-no-print" style={{ padding: '12px 16px', background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '10px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                                <div>
                                    <strong style={{ color: '#065f46', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <i className="bi bi-check-circle-fill"></i>
                                        Nota de Venta {notaEmitida.numero} emitida exitosamente
                                    </strong>
                                    <div style={{ color: '#047857', fontSize: '0.84rem', marginTop: 2 }}>
                                        Recibo oficial N° <strong>{notaEmitida.recibo_numero || '(Asignado)'}</strong> · Sucursal <strong>{notaEmitida.sucursal}</strong>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                    {/* Selector de formato: Talonario Oficial vs Ticket */}
                                    <div style={{ display: 'flex', background: '#e2e8f0', padding: '3px', borderRadius: '8px', gap: 4 }}>
                                        <button
                                            type="button"
                                            onClick={() => setFormatoVista('recibo')}
                                            style={{
                                                padding: '5px 12px', borderRadius: 6, border: 'none',
                                                background: formatoVista === 'recibo' ? '#164e9e' : 'transparent',
                                                color: formatoVista === 'recibo' ? '#fff' : '#334155',
                                                fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', gap: 4
                                            }}
                                        >
                                            <i className="bi bi-file-earmark-check"></i>
                                            Recibo (Talonario)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setFormatoVista('ticket')}
                                            style={{
                                                padding: '5px 12px', borderRadius: 6, border: 'none',
                                                background: formatoVista === 'ticket' ? '#0f172a' : 'transparent',
                                                color: formatoVista === 'ticket' ? '#fff' : '#334155',
                                                fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', gap: 4
                                            }}
                                        >
                                            <i className="bi bi-receipt"></i>
                                            Ticket Térmico
                                        </button>
                                    </div>

                                    <button className="mf-btn" onClick={imprimirTicket} style={{ background: '#164e9e', borderColor: '#164e9e' }}>
                                        <i className="bi bi-printer me-1"></i>Imprimir {formatoVista === 'recibo' ? 'Recibo Oficial' : 'Ticket'}
                                    </button>
                                    <button className="mf-btn sec" onClick={onClose}>Cerrar</button>
                                </div>
                            </div>

                            {/* VISTA SEGÚN EL FORMATO SELECCIONADO */}
                            {formatoVista === 'recibo' ? (
                                <div className="nv-print-area">
                                    <ReciboVentaDocumento
                                        datos={{
                                            ...notaEmitida,
                                            ...(notaEmitida.recibo_datos || datosRecibo),
                                            numero: notaEmitida.recibo_numero || notaEmitida.numero,
                                            total: Number(notaEmitida.total) || 0,
                                            abono: Number(notaEmitida.abono !== undefined ? notaEmitida.abono : (abono !== null ? abono : notaEmitida.total)),
                                            saldo: Number(notaEmitida.saldo !== undefined ? notaEmitida.saldo : Math.max(0, Number(notaEmitida.total || 0) - (abono !== null ? abono : Number(notaEmitida.total || 0))))
                                        }}
                                        editable={false}
                                    />
                                </div>
                            ) : (
                                /* DOCUMENTO IMPRIMIBLE / TICKET TÉRMICO */
                                <div className="nv-print-area" ref={ticketRef} style={{ background: '#fff', border: '1px dashed #cbd5e1', borderRadius: '12px', padding: '24px', maxWidth: '600px', margin: '0 auto', fontFamily: 'monospace, Arial, sans-serif' }}>
                                    <div style={{ textAlign: 'center', borderBottom: '2px solid #000', paddingBottom: '12px', marginBottom: '14px' }}>
                                        <h2 style={{ margin: '0 0 4px', fontSize: '1.5rem', fontWeight: '900', letterSpacing: '1px' }}>VISION CLICK</h2>
                                        <div style={{ fontWeight: '700', fontSize: '0.95rem', textTransform: 'uppercase' }}>{infoSucursal.nombre}</div>
                                        <div style={{ fontSize: '0.85rem', color: '#333' }}>{infoSucursal.direccion}</div>
                                        <div style={{ fontSize: '0.85rem', color: '#333' }}>Tel: {infoSucursal.telefono}</div>
                                        <div style={{ marginTop: '8px', display: 'inline-block', padding: '4px 12px', background: '#000', color: '#fff', fontWeight: '800', borderRadius: '4px', fontSize: '0.95rem' }}>
                                            NOTA DE VENTA: {notaEmitida.numero}
                                        </div>
                                    </div>

                                    <div style={{ fontSize: '0.88rem', lineHeight: '1.5', marginBottom: '14px' }}>
                                        <div><strong>FECHA:</strong> {notaEmitida.fecha_emision || new Date(notaEmitida.created_at).toLocaleDateString()}</div>
                                        <div><strong>CLIENTE:</strong> {notaEmitida.cliente_nombre}</div>
                                        <div><strong>R.U.C. / C.I.:</strong> {notaEmitida.cliente_identificacion}</div>
                                        {notaEmitida.cliente_telefono && <div><strong>TELÉFONO:</strong> {notaEmitida.cliente_telefono}</div>}
                                        {notaEmitida.cliente_direccion && <div><strong>DIRECCIÓN:</strong> {notaEmitida.cliente_direccion}</div>}
                                        <div><strong>FORMA DE PAGO:</strong> {FORMAS_PAGO[notaEmitida.forma_pago] || notaEmitida.forma_pago || 'Efectivo'}</div>
                                        {notaEmitida.emitida_por && <div><strong>ATENDIDO POR:</strong> {notaEmitida.emitida_por}</div>}
                                    </div>

                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '14px' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '1px solid #000', borderTop: '1px solid #000', textAlign: 'left' }}>
                                                <th style={{ padding: '6px 2px' }}>CANT.</th>
                                                <th style={{ padding: '6px 2px' }}>DESCRIPCIÓN</th>
                                                <th style={{ padding: '6px 2px', textAlign: 'right' }}>P. UNIT</th>
                                                <th style={{ padding: '6px 2px', textAlign: 'right' }}>TOTAL</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(notaEmitida.items || []).map((it, idx) => (
                                                <tr key={idx} style={{ borderBottom: '1px dotted #ccc' }}>
                                                    <td style={{ padding: '6px 2px' }}>{it.cantidad}</td>
                                                    <td style={{ padding: '6px 2px' }}>{it.descripcion}</td>
                                                    <td style={{ padding: '6px 2px', textAlign: 'right' }}>{dinero(it.precio)}</td>
                                                    <td style={{ padding: '6px 2px', textAlign: 'right', fontWeight: '700' }}>{dinero(it.total || it.subtotal || (it.cantidad * it.precio))}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                                        <table style={{ width: '220px', fontSize: '0.9rem', lineHeight: '1.4' }}>
                                            <tbody>
                                                <tr><td>SUBTOTAL:</td><td style={{ textAlign: 'right' }}>{dinero(notaEmitida.subtotal)}</td></tr>
                                                <tr><td>IVA:</td><td style={{ textAlign: 'right' }}>{dinero(notaEmitida.iva)}</td></tr>
                                                <tr style={{ borderTop: '1px solid #000', fontWeight: '900', fontSize: '1.05rem' }}>
                                                    <td style={{ paddingTop: '4px' }}>TOTAL:</td>
                                                    <td style={{ textAlign: 'right', paddingTop: '4px' }}>{dinero(notaEmitida.total)}</td>
                                                </tr>
                                                <tr><td>ABONO:</td><td style={{ textAlign: 'right', color: '#059669', fontWeight: '800' }}>{dinero(Number(notaEmitida.abono) || Number(notaEmitida.saldo) ? notaEmitida.abono : notaEmitida.total)}</td></tr>
                                                <tr><td>SALDO:</td><td style={{ textAlign: 'right', color: '#dc2626', fontWeight: '900' }}>{dinero(notaEmitida.saldo || 0)}</td></tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    {notaEmitida.observaciones && (
                                        <div style={{ fontSize: '0.8rem', color: '#555', borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginBottom: '12px' }}>
                                            <strong>Observaciones:</strong> {notaEmitida.observaciones}
                                        </div>
                                    )}

                                    <div style={{ textAlign: 'center', fontSize: '0.8rem', color: '#555', borderTop: '1px dashed #000', paddingTop: '10px' }}>
                                        ¡Gracias por su preferencia! · Cuidamos de su visión
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* FORMULARIO DE EMISIÓN DE NOTA DE VENTA */
                        <div>
                            {/* Indicador de Sucursal Asignada */}
                            <div style={{ fontSize: '0.85rem', color: '#374151', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <i className="bi bi-shop"></i>
                                <span>Se emitirá desde la sucursal <strong>{sucursal}</strong> con numeración secuencial propia ({sucursal === 'Tulcán Norte' ? 'NV-NOR-...' : 'NV-SUR-...'}).</span>
                            </div>

                            {mensaje && !mensaje.ok && (
                                <div className="mf-aviso" style={{ background: '#fee2e2', color: '#991b1b' }}>
                                    <i className="bi bi-x-circle me-1"></i>{mensaje.texto}
                                </div>
                            )}

                            {/* Sección Cliente */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <div className="mf-sec">Cliente</div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.88rem', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={consumidorFinal}
                                        onChange={e => {
                                            const cf = e.target.checked;
                                            setConsumidorFinal(cf);
                                            setErrores({});
                                            if (cf) {
                                                setCliente({ identificacion: CONSUMIDOR_FINAL, nombre: 'CONSUMIDOR FINAL', direccion: '', email: '', telefono: '' });
                                                setPacienteId(null);
                                            } else if (paciente) {
                                                elegirPaciente(paciente);
                                            } else {
                                                setCliente({ identificacion: '', nombre: '', direccion: '', email: '', telefono: '' });
                                            }
                                        }}
                                    />
                                    Consumidor final
                                </label>
                            </div>

                            {!consumidorFinal && (
                                <>
                                    {!paciente && (
                                        <div className="mf-f mf-busca" style={{ marginBottom: 10 }}>
                                            <label>Buscar paciente (opcional)</label>
                                            <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Nombre o cédula..." />
                                            {resultados.length > 0 && (
                                                <div className="mf-res">
                                                    {resultados.map(p => (
                                                        <div key={p.id} onClick={() => elegirPaciente(p)}>{p.first_name} {p.last_name} · {p.cedula || 'S/C'}</div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="mf-grid">
                                        <div className="mf-f">
                                            <label>Cédula / RUC / Pasaporte</label>
                                            <input className={errores.identificacion ? 'bad' : ''} value={cliente.identificacion} onChange={e => cambiarCliente('identificacion', e.target.value.trim())} />
                                            {err('identificacion')}
                                        </div>
                                        <div className="mf-f" style={{ gridColumn: 'span 2' }}>
                                            <label>Nombre / Razón social</label>
                                            <input className={errores.nombre ? 'bad' : ''} value={cliente.nombre} onChange={e => cambiarCliente('nombre', e.target.value.toUpperCase())} />
                                            {err('nombre')}
                                        </div>
                                        <div className="mf-f"><label>Correo</label><input type="email" value={cliente.email} onChange={e => cambiarCliente('email', e.target.value)} /></div>
                                        <div className="mf-f"><label>Teléfono</label><input value={cliente.telefono} onChange={e => cambiarCliente('telefono', e.target.value)} /></div>
                                        <div className="mf-f"><label>Dirección</label><input value={cliente.direccion} onChange={e => cambiarCliente('direccion', e.target.value)} /></div>
                                    </div>
                                </>
                            )}

                            {/* Sección Productos y Servicios */}
                            <div className="mf-sec" style={{ marginTop: 18 }}>Productos y servicios</div>
                            {autocompletado && (
                                <div style={{ fontSize: '0.82rem', color: '#065f46', background: '#ecfdf5', border: '1px solid #a7f3d0', padding: '7px 12px', borderRadius: 8, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <i className="bi bi-check-circle-fill"></i>
                                    <span>Tratamiento cargado automáticamente desde la ficha del paciente.</span>
                                </div>
                            )}

                            <div className="mf-f mf-busca" style={{ marginBottom: 10 }}>
                                <input
                                    value={busquedaCat}
                                    onChange={e => setBusquedaCat(e.target.value)}
                                    placeholder="Buscar en servicios e inventario (examen, armazón, lunas...)"
                                />
                                {busquedaCat.trim() && catalogo.length > 0 && (
                                    <div className="mf-res" style={{ maxHeight: 260, overflowY: 'auto' }}>
                                        {catalogo.map((c, k) => (
                                            <div key={k} onClick={() => agregarDelCatalogo(c)} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                                                <span>
                                                    <span style={{ fontSize: '0.7rem', fontWeight: 700, color: c.tipo === 'Servicio' ? '#1d4ed8' : '#8a7330', marginRight: 6 }}>{c.tipo.toUpperCase()}</span>
                                                    {c.descripcion}
                                                    {c.stock !== undefined && <span style={{ color: '#6b7280', fontSize: '0.75rem' }}> · stock {Number(c.stock)}</span>}
                                                </span>
                                                <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{dinero(c.precio)} · IVA {c.iva}%</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {busquedaCat.trim() && catalogo.length === 0 && (
                                    <div className="mf-res"><div style={{ color: '#6b7280', cursor: 'default' }}>Nada encontrado. Puede escribir el ítem a mano abajo.</div></div>
                                )}
                            </div>
                            {errores.items && <div className="mf-err">* {errores.items}</div>}

                            <div style={{ overflowX: 'auto', width: '100%' }}>
                                <table className="mf-t">
                                    <thead>
                                        <tr>
                                            <th style={{ minWidth: 200 }}>Descripción</th>
                                            <th style={{ width: 70, minWidth: 65 }}>Cant.</th>
                                            <th style={{ width: 110, minWidth: 100 }}>Precio unit.</th>
                                            <th style={{ width: 110, minWidth: 100 }}>Descuento $</th>
                                            <th style={{ width: 95, minWidth: 92 }}>IVA</th>
                                            <th style={{ width: 95, minWidth: 90, textAlign: 'right' }}>Subtotal</th>
                                            <th style={{ width: 38, minWidth: 38 }}></th>
                                        </tr>
                                    </thead>
                                <tbody>
                                    {items.map((it, i) => (
                                        <tr key={i}>
                                            <td>
                                                <input
                                                    className={errores[`items.${i + 1}`] ? 'bad' : ''}
                                                    value={it.descripcion}
                                                    onChange={e => cambiarItem(i, 'descripcion', e.target.value)}
                                                    placeholder="Ej: Armazón + lunas monofocales"
                                                />
                                                {errores[`items.${i + 1}`] && <div className="mf-err">* {errores[`items.${i + 1}`]}</div>}
                                                {it.producto_id && (
                                                    <div style={{ fontSize: '0.74rem', marginTop: 2, color: Number(it.cantidad) > it.stock ? '#dc2626' : '#8a7330', fontWeight: 600 }}>
                                                        <i className="bi bi-box-seam me-1"></i>Inventario · stock {it.stock}
                                                        {Number(it.cantidad) > it.stock ? ' · no alcanza' : ' · se descontará al emitir'}
                                                    </div>
                                                )}
                                            </td>
                                            <td><input type="number" min="1" step="1" value={it.cantidad} onChange={e => cambiarItem(i, 'cantidad', e.target.value)} /></td>
                                            <td><input type="number" min="0" step="0.01" value={it.precio} onChange={e => cambiarItem(i, 'precio', e.target.value)} /></td>
                                            <td><input type="number" min="0" step="0.01" value={it.descuento} onChange={e => cambiarItem(i, 'descuento', e.target.value)} /></td>
                                            <td>
                                                <select value={it.iva} onChange={e => cambiarItem(i, 'iva', Number(e.target.value))}>
                                                    <option value={0}>0%</option>
                                                    <option value={5}>5%</option>
                                                    <option value={12}>12%</option>
                                                    <option value={14}>14%</option>
                                                    <option value={15}>15%</option>
                                                </select>
                                            </td>
                                            <td style={{ textAlign: 'right', paddingTop: 12 }}>{dinero(lineas[i]?.base)}</td>
                                            <td>
                                                {items.length > 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setItems(prev => prev.filter((_, k) => k !== i))}
                                                        title="Quitar de esta nota de venta"
                                                        style={{ border: 'none', background: 'transparent', color: '#6b7280', cursor: 'pointer', paddingTop: 8 }}
                                                    >
                                                        <i className="bi bi-x-lg"></i>
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>

                            <div style={{ marginTop: 8 }}>
                                <button
                                    type="button"
                                    className="mf-btn sec"
                                    style={{ fontSize: '0.8rem', padding: '5px 12px' }}
                                    onClick={() => setItems(prev => [...prev, nuevoItem(15)])}
                                >
                                    <i className="bi bi-plus-lg me-1"></i>Agregar línea en blanco
                                </button>
                            </div>

                            {/* SECCIÓN OPCIONAL: DETALLE ÓPTICO / RECIBO DE TALONARIO */}
                            <div style={{
                                marginTop: 16,
                                padding: '12px 14px',
                                background: '#f8fafc',
                                border: '1.5px solid #cbd5e1',
                                borderRadius: 10
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontWeight: 800, fontSize: '0.86rem', color: '#164e9e', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <i className="bi bi-file-earmark-medical"></i>
                                        Detalle Óptico para el Recibo Oficial (Talonario)
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setMostrarDetalleOptico(prev => !prev)}
                                        style={{
                                            border: 'none', background: 'transparent',
                                            color: '#164e9e', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer'
                                        }}
                                    >
                                        {mostrarDetalleOptico ? '▲ Ocultar detalles' : '▼ Completar refracción y lentes'}
                                    </button>
                                </div>

                                {mostrarDetalleOptico && (
                                    <div style={{ marginTop: 12 }}>
                                        <div style={{ marginBottom: 10 }}>
                                            <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: 2 }}>Armazón:</label>
                                            <input
                                                value={datosRecibo.armazon || ''}
                                                onChange={e => setDatosRecibo(prev => ({ ...prev, armazon: e.target.value }))}
                                                placeholder="Marca / Código del armazón..."
                                                style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.86rem' }}
                                            />
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: '0.82rem' }}>
                                            <div style={{ background: '#fff', padding: 8, borderRadius: 6, border: '1px solid #e2e8f0' }}>
                                                <strong>Ojo Derecho (OD):</strong>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4, marginTop: 4 }}>
                                                    <input placeholder="Esf" value={datosRecibo.esfera_od || ''} onChange={e => setDatosRecibo(p => ({ ...p, esfera_od: e.target.value }))} />
                                                    <input placeholder="Cil" value={datosRecibo.cilindro_od || ''} onChange={e => setDatosRecibo(p => ({ ...p, cilindro_od: e.target.value }))} />
                                                    <input placeholder="Eje" value={datosRecibo.eje_od || ''} onChange={e => setDatosRecibo(p => ({ ...p, eje_od: e.target.value }))} />
                                                    <input placeholder="Add" value={datosRecibo.adicion_od || ''} onChange={e => setDatosRecibo(p => ({ ...p, adicion_od: e.target.value }))} />
                                                    <input placeholder="DP" value={datosRecibo.dp_od || ''} onChange={e => setDatosRecibo(p => ({ ...p, dp_od: e.target.value }))} />
                                                </div>
                                            </div>

                                            <div style={{ background: '#fff', padding: 8, borderRadius: 6, border: '1px solid #e2e8f0' }}>
                                                <strong>Ojo Izquierdo (OI):</strong>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 4, marginTop: 4 }}>
                                                    <input placeholder="Esf" value={datosRecibo.esfera_oi || ''} onChange={e => setDatosRecibo(p => ({ ...p, esfera_oi: e.target.value }))} />
                                                    <input placeholder="Cil" value={datosRecibo.cilindro_oi || ''} onChange={e => setDatosRecibo(p => ({ ...p, cilindro_oi: e.target.value }))} />
                                                    <input placeholder="Eje" value={datosRecibo.eje_oi || ''} onChange={e => setDatosRecibo(p => ({ ...p, eje_oi: e.target.value }))} />
                                                    <input placeholder="Add" value={datosRecibo.adicion_oi || ''} onChange={e => setDatosRecibo(p => ({ ...p, adicion_oi: e.target.value }))} />
                                                    <input placeholder="DP" value={datosRecibo.dp_oi || ''} onChange={e => setDatosRecibo(p => ({ ...p, dp_oi: e.target.value }))} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Forma de pago y Totales */}
                            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: 20, marginTop: 16, alignItems: 'start' }}>
                                <div>
                                    <div className="mf-sec">Forma de Pago y Abono</div>
                                    <div className="mf-f" style={{ marginBottom: 10 }}>
                                        <label>Forma de pago</label>
                                        <select value={formaPago} onChange={e => setFormaPago(e.target.value)}>
                                            {Object.entries(FORMAS_PAGO).map(([cod, nom]) => (
                                                <option key={cod} value={cod}>{nom}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* ABONO Y SALDO */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                                        <div className="mf-f">
                                            <label style={{ color: '#059669', fontWeight: 800 }}>Abono ($):</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={abono !== null ? abono : total}
                                                onChange={e => setAbono(e.target.value !== '' ? Number(e.target.value) : 0)}
                                            />
                                        </div>
                                        <div className="mf-f">
                                            <label style={{ color: '#dc2626', fontWeight: 800 }}>Saldo pendiente ($):</label>
                                            <input
                                                type="text"
                                                readOnly
                                                value={dinero(Math.max(0, total - (abono !== null ? Number(abono) : total)))}
                                                style={{ background: '#fef2f2', fontWeight: 800, color: '#dc2626' }}
                                            />
                                        </div>
                                    </div>

                                    <div className="mf-f">
                                        <label>Observaciones (opcional)</label>
                                        <input
                                            value={observaciones}
                                            onChange={e => setObservaciones(e.target.value)}
                                            placeholder="Detalle o notas adicionales para el comprobante..."
                                        />
                                    </div>
                                </div>

                                <div className="mf-tot">
                                    <table style={{ width: '100%', maxWidth: 280 }}>
                                        <tbody>
                                            <tr><td>Subtotal:</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{dinero(subtotal)}</td></tr>
                                            <tr><td>IVA (15%):</td><td style={{ textAlign: 'right', fontWeight: 600 }}>{dinero(ivaTotal)}</td></tr>
                                            <tr style={{ borderTop: '2px solid #0f172a', fontSize: '1.15rem', fontWeight: 900 }}>
                                                <td style={{ paddingTop: 6 }}>TOTAL:</td>
                                                <td style={{ textAlign: 'right', paddingTop: 6, color: '#065f46' }}>{dinero(total)}</td>
                                            </tr>
                                            <tr><td style={{ color: '#059669', fontWeight: 700 }}>Abono:</td><td style={{ textAlign: 'right', color: '#059669', fontWeight: 800 }}>{dinero(abono !== null ? abono : total)}</td></tr>
                                            <tr><td style={{ color: '#dc2626', fontWeight: 700 }}>Saldo:</td><td style={{ textAlign: 'right', color: '#dc2626', fontWeight: 800 }}>{dinero(Math.max(0, total - (abono !== null ? Number(abono) : total)))}</td></tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, borderTop: '1px solid #e5e7eb', paddingTop: 14 }}>
                                <button type="button" className="mf-btn sec" onClick={onClose} disabled={emitiendo}>
                                    Cancelar
                                </button>
                                <button type="button" className="mf-btn" onClick={emitir} disabled={emitiendo || total <= 0} style={{ background: '#164e9e', borderColor: '#164e9e' }}>
                                    <i className="bi bi-file-earmark-check me-1"></i>
                                    {emitiendo ? 'Emitiendo nota...' : `Emitir Nota y Recibo (${dinero(total)})`}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ModalNotaVenta;
